// src/pages/settings/SecuritySettings.jsx
// Account security page letting the signed-in user change their own password

import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";

import { authService } from "../../api/authService";

// Mirrors _PASSWORD_PATTERN / _PASSWORD_ERROR in backend/app/schemas/auth.py.
// Keep both in sync: the backend rejects anything this misses, but validating
// here saves the user a round trip.
const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]).{8,}$/;

const PASSWORD_ERROR =
  "Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter, one number, and one special character.";

export default function SecuritySettings() {
  const theme = useTheme();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const visibilityAdornment = (
    <InputAdornment position="end">
      <IconButton
        onClick={() => setShowPassword((prev) => !prev)}
        edge="end"
        size="small"
        aria-label={showPassword ? "Hide passwords" : "Show passwords"}
        tabIndex={-1}
      >
        {showPassword ? (
          <VisibilityOffRoundedIcon fontSize="small" />
        ) : (
          <VisibilityRoundedIcon fontSize="small" />
        )}
      </IconButton>
    </InputAdornment>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      setError("The new password must be different from your current password.");
      return;
    }
    if (!PASSWORD_PATTERN.test(newPassword)) {
      setError(PASSWORD_ERROR);
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword({
        currentPassword,
        newPassword,
        confirmPassword,
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess(
        "Your password has been updated. Other devices have been signed out."
      );
    } catch (err) {
      setError(err.message || "Could not change your password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Security
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Change the password you use to sign in.
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 520 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LockResetRoundedIcon color="primary" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Change password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You'll stay signed in here. Any other device will be signed out.
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
            <TextField
              label="Current password"
              type={showPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              autoComplete="current-password"
              disabled={loading}
              InputProps={{ endAdornment: visibilityAdornment }}
            />

            <TextField
              label="New password"
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
              disabled={loading}
              helperText="At least 8 characters, with an uppercase letter, a lowercase letter, a number, and a special character."
              InputProps={{ endAdornment: visibilityAdornment }}
            />

            <TextField
              label="Confirm new password"
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
              disabled={loading}
              InputProps={{ endAdornment: visibilityAdornment }}
            />

            <Box>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <LockResetRoundedIcon />
                  )
                }
              >
                {loading ? "Updating..." : "Update password"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
